import {
  CoinImageConfig,
  SvgElementToImgParams,
  SvgElementToImgResponse
} from '@/models/image-utils';
import { isSafari } from './is-safari';

export const dataURItoBlob = (dataURI: string) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  var byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  var mimeString = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  var ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  var ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  var blob = new Blob([ab], { type: mimeString });
  return blob;
};

export const getBase64FromUrl = async (url: string): Promise<string> => {
  const data = await fetch(url);
  const blob = await data.blob();

  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result as string;
      resolve(base64data);
    };
  });
};

export const getBlobFromUrl = async (url: string): Promise<Blob> => {
  const data = await fetch(url);
  const blob = await data.blob();

  return new Promise(resolve => {
    resolve(blob);
  });
};

export const baseCoinImageConfig: CoinImageConfig = {
  typeOfImg: 'png',
  width: '1024',
  height: '1024'
};

export const svgElementToImgSafari = (
  params: SvgElementToImgParams
): Promise<SvgElementToImgResponse> => {
  return new Promise(async resolve => {
    const { svgElement, typeOfImg, width, height } = params;
    const cloneSvgElement = svgElement.cloneNode(true) as HTMLElement &
      SVGElement;
    cloneSvgElement.setAttribute('width', width);
    cloneSvgElement.setAttribute('height', height);

    const encodedData = window.btoa(cloneSvgElement.outerHTML);

    const urlSvg = `data:image/svg+xml;base64,${encodedData}`;

    let img = new Image();

    img.src = urlSvg;
    await fetch(img.src);

    const canvas = document.createElement('canvas') as HTMLCanvasElement;
    const widthCanvas = (parseFloat(width) + 4).toString();
    const heightCanvas = (parseFloat(width) + 4).toString();

    const canvasContext = canvas.getContext('2d') as CanvasRenderingContext2D;

    canvas.setAttribute('width', widthCanvas);
    canvas.setAttribute('height', heightCanvas);
    canvas.setAttribute('background', 'transparent');

    canvas.style.lineHeight = '48px';
    canvas.style.fontFamily = `'Inter', sans-serif`;
    canvas.style.fontWeight = '600';
    canvas.style.fontSize = '40px';
    canvasContext.clearRect(0, 0, canvas.width, canvas.height);
    canvasContext.imageSmoothingEnabled = true;

    setTimeout(() => {
      canvasContext.drawImage(img, 2, 2);
      canvas.toBlob(blobGenerated => {
        const blob = blobGenerated as Blob;
        resolve({ blob, canvas });
      }, typeOfImg);
    }, 0);
  });
};

export const svgElementToImgDefault = (
  params: SvgElementToImgParams
): Promise<SvgElementToImgResponse> => {
  return new Promise(resolve => {
    const { svgElement, typeOfImg, width, height } = params;
    const cloneSvgElement = svgElement.cloneNode(true) as HTMLElement &
      SVGElement;
    cloneSvgElement.setAttribute('width', width);
    cloneSvgElement.setAttribute('height', height);

    const urlSvg = `data:image/svg+xml,${encodeURIComponent(
      cloneSvgElement.outerHTML
    )}`;

    const canvas = document.createElement('canvas') as HTMLCanvasElement;
    const widthCanvas = (parseFloat(width) + 4).toString();
    const heightCanvas = (parseFloat(width) + 4).toString();

    canvas.setAttribute('width', widthCanvas);
    canvas.setAttribute('height', heightCanvas);
    canvas.setAttribute('background', 'transparent');

    const canvasContext = canvas.getContext('2d') as CanvasRenderingContext2D;

    let img = new Image();
    img.src = urlSvg;

    img.onload = () => {
      canvas.style.lineHeight = '48px';
      canvasContext.clearRect(0, 0, canvas.width, canvas.height);
      canvasContext.imageSmoothingEnabled = true;
      canvasContext.font = '600 40px Inter';
      canvasContext.drawImage(img, 2, 2);

      canvas.toBlob(blobGenerated => {
        const blob = blobGenerated as Blob;
        resolve({ blob, canvas });
      }, typeOfImg);
    };
  });
};

export const svgElementToImg = (
  params: SvgElementToImgParams
): Promise<SvgElementToImgResponse> => {
  if (isSafari()) {
    return svgElementToImgSafari(params);
  }

  return svgElementToImgDefault(params);
};

export function preload(sources: any[]) {
  for (var i = 0; i < sources.length; i++) {
    let image = new Image();
    image.src = sources[i];
  }
}

export function preloadWithPromise(src: string) {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.onload = () => resolve(img.height);
    img.onerror = reject;
    img.src = src;
  });
}
